import "intersection-observer"

import React, { useEffect, useState } from "react"
import { Check, ChevronsDown, Plus } from "react-feather"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { AnalyticsEvent, trackEvent } from "~/utils/analytics/analytics.client"
import { useCookie } from "~/utils/cookies"

import { BookmarkButton } from "./BookmarkButton"

export type BookmarkerProps = {
  articleUID: string
}

export const Bookmarker: React.FC<BookmarkerProps> = ({ articleUID }) => {
  const { t } = useTranslation("articlePage")

  const cookieName = `marca_de_leitura_${articleUID}`
  const [bookmarkValue, setCookie, removeCookie] = useCookie<string | number>(
    cookieName,
  )

  const [allSliceZoneItems, setSliceZoneItems] = useState<Element[]>([])
  const [elementInView, setElementInView] = useState(0)

  const bookmarkedElementIndex =
    typeof bookmarkValue === "number"
      ? bookmarkValue
      : parseInt(bookmarkValue || "-1", 10)
  const bookmarkedElement = allSliceZoneItems.find(
    (el, index) => index === bookmarkedElementIndex,
  )

  useEffect(() => {
    setSliceZoneItems(
      [...document.querySelectorAll("#article-slice-zone p")].filter(
        (element) => element.textContent,
      ),
    )
  }, [])

  useEffect(() => {
    if (!allSliceZoneItems.length) return

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const indexes = allSliceZoneItems.reduce((acc, p, index) => {
          const isOnScreen = entries.some(
            (entry) => entry.isIntersecting && entry.target === p,
          )

          return isOnScreen ? [...acc, index] : acc
        }, [] as number[])

        const middleIndex = indexes[Math.floor(indexes.length / 2)]

        if (middleIndex) setElementInView(middleIndex)
      },
      { threshold: 0 },
    )

    allSliceZoneItems.forEach((el) => intersectionObserver.observe(el))

    return () => {
      intersectionObserver.disconnect()
    }
  }, [allSliceZoneItems])

  if (elementInView >= allSliceZoneItems.length - 1) {
    return null
  }

  const hasReadABit = elementInView > 2
  const isBeforeBookmark = bookmarkedElementIndex > elementInView
  const isThisParagraphInView = bookmarkedElementIndex === elementInView

  const handleSaveBookmark = () => {
    if (!elementInView) return

    if (elementInView === bookmarkedElementIndex) {
      toast(t("bookmark.removed"))
      removeCookie()

      return
    }

    toast(t("bookmark.added"))

    setCookie(elementInView)
    trackEvent(AnalyticsEvent.ARTICLE_ADD_BOOKMARK)
  }

  if (isBeforeBookmark) {
    if (!bookmarkedElement) return null

    return (
      <BookmarkButton
        onClick={() => {
          bookmarkedElement.scrollIntoView()
          // To compensate for the sticky header
          window.scrollBy(0, -100)
        }}
      >
        <ChevronsDown size={16} strokeWidth={3} />
      </BookmarkButton>
    )
  }

  if (!hasReadABit) return null

  const SmallerIcon = isThisParagraphInView ? Check : Plus

  return (
    <BookmarkButton onClick={handleSaveBookmark}>
      <SmallerIcon size={16} strokeWidth={3} />
    </BookmarkButton>
  )
}
